import { parsePhoneNumberFromString as parseNumber } from 'libphonenumber-js';
import {
  NO_HTML_TAGS_REGEX,
  UNSUPPORTED_CHARS,
  ALPHANUMERIC_REGEX,
  PHONE_EXT_REGEX,
  POSTAL_CODE_REGEX,
  URL_REGEX,
  URL_PROTOCOL_REGEX,
  LINKEDIN_REGEX,
  LINKEDIN_DOMAIN_REGEX,
  BANNED_WORDS_REGEX,
  EMAIL_INVALID_CHARS_REGEX,
  EMAIL_HOST_NAME_REGEX,
  EMAIL_LOCAL_NAME_REGEX,
} from './inputRegex';

const MAX_BYTE_LENGTH = 128;

const getByteLength = (str) => new Blob([str]).size;

const _noHtmlTags = (value) => {
  return value?.length && !value.match(NO_HTML_TAGS_REGEX);
};

const firstName = (value) => {
  if (!value.length) return 'FIRST_NAME_1';
  if (value.match(UNSUPPORTED_CHARS)) return 'FIRST_NAME_2';
  if (!value.match(ALPHANUMERIC_REGEX)) return 'FIRST_NAME_3';
  if (getByteLength(value) > MAX_BYTE_LENGTH) return 'FIRST_NAME_4';
  if (value.match(BANNED_WORDS_REGEX)) return 'FIRST_NAME_5';

  return '';
}

const lastName = (value) => {
  if (!value.length) return 'LAST_NAME_1';
  if (value.match(UNSUPPORTED_CHARS)) return 'LAST_NAME_2';
  if (!value.match(ALPHANUMERIC_REGEX)) return 'LAST_NAME_3';
  if (getByteLength(value) > MAX_BYTE_LENGTH) return 'LAST_NAME_4';
  if (value.match(BANNED_WORDS_REGEX)) return 'LAST_NAME_5';

  return '';
}

const email = (value) => {
  if (!value.length) return '';
  if (value.match(EMAIL_INVALID_CHARS_REGEX)) return 'EMAIL';

  const emailParts = value.split('@');
  if (emailParts.length !== 2) return 'EMAIL';

  if (
    emailParts[0].match(EMAIL_LOCAL_NAME_REGEX) &&
    emailParts[1].match(EMAIL_HOST_NAME_REGEX)
  ) {
    return '';
  }
  return 'EMAIL';
};

/**
 * Formats website/blog URL by lowercasing domain and trimming
 * To be used when posting to API
 * @param {String} url
 */
const formatWebsiteUrl = (url) => {
  if (url === '') return url;

  const formattedUrl = url.trim().toLowerCase();
  if (!formattedUrl.match(URL_REGEX)) return false;

  // Add protocol if necessary to create valid URL
  const urlWithProtocol = url.match(URL_PROTOCOL_REGEX)
    ? url
    : `https://${url}`;
  const { hostname } = new URL(urlWithProtocol);

  const hostnameRegex = new RegExp(hostname, 'i');

  return url
    .trim()
    .replace(URL_PROTOCOL_REGEX, (match) => match.toLowerCase())
    .replace(hostnameRegex, (match) => match.toLowerCase());
};

const website = (value) => {
  const formattedWebsite = formatWebsiteUrl(value);

  if (!formattedWebsite) return 'WEBSITE';

  return '';
};

const blog = (value) => {
  const formattedBlog = formatWebsiteUrl(value);

  if (!formattedBlog) return 'BLOG';

  return '';
};

const linkedIn = (value) => {
  if (value?.length && value.length > 255) {
    return 'LINKEDIN';
  }

  if (value?.length && !value.match(LINKEDIN_REGEX)) {
    return 'LINKEDIN';
  }

  return '';
};

/**
 * Formats LinkedIn url by lowercasing domain and trimming
 * To be used when posting to API
 * @param {String} url
 */
const formatLinkedIn = (url) => {
  return url
    .trim()
    .replace(LINKEDIN_DOMAIN_REGEX, (match) => match.toLowerCase());
};

const jobTitle = (value) => {
  return _noHtmlTags(value) ? 'JOB_TITLE' : '';
};
const jobField = (jobTitle, jobList) => {
  // allows jobTitle field to be empty when saving
  if (!jobTitle.length) return '';

  // jobTitle should be present in the jobList
  if (!jobList.includes(jobTitle)) {
    return 'JOB_TITLE_NO_MATCH';
  }

  return '';
};
const company = (value) => {
  return _noHtmlTags(value) ? 'COMPANY' : '';
};
const industryOther = (value) => {
  return _noHtmlTags(value) ? 'INDUSTRY' : '';
};

const address1 = (value) => {
  return _noHtmlTags(value) ? 'ADDRESS_1' : '';
};

const address2 = (value) => {
  return _noHtmlTags(value) ? 'ADDRESS_2' : '';
};

const city = (value) => {
  return _noHtmlTags(value) ? 'CITY' : '';
};

const state = (value) => {
  return _noHtmlTags(value) ? 'STATE' : '';
};

const postalCode = (value) => {
  if (value?.length && !value.match(POSTAL_CODE_REGEX)) {
    return 'ZIPCODE';
  }
  return '';
};

const phone = (number, countryCode) => {
  const parsed = parseNumber(number, countryCode.toUpperCase());
  if (!number || parsed?.isValid()) {
    return '';
  } else {
    return 'PHONE_NUMBER';
  }
};

const phoneExt = (value) => {
  if (value?.length && !value.match(PHONE_EXT_REGEX)) {
    return 'PHONE_NUMBER_EXT';
  } else {
    return '';
  }
};

const aboutMe = (value) => {
  return _noHtmlTags(value) ? 'ABOUT_ME' : '';
};

export default {
  _noHtmlTags,
  firstName,
  lastName,
  email,
  phoneExt,
  website,
  blog,
  formatWebsiteUrl,
  jobTitle,
  jobField,
  company,
  industryOther,
  linkedIn,
  formatLinkedIn,
  address1,
  address2,
  city,
  state,
  postalCode,
  phone,
  aboutMe,
};
